import type { CalendarResource } from '@/api/resourceCalendarApi'
import { avatarTone, dayParts, formatHours, weekSummary } from '@/lib/myTasks'
import { heatClass, utilization } from '@/lib/myTasksViews'
import { cn } from '@/lib/utils'

const LEGEND: { label: string; pct: number | null }[] = [
  { label: 'No shift', pct: null },
  { label: 'Free', pct: 0 },
  { label: 'Light', pct: 20 },
  { label: 'Busy', pct: 60 },
  { label: 'Nearly full', pct: 90 },
  { label: 'Over', pct: 120 },
]

/**
 * Who has room and who is over, as a heatmap: one row per person, one cell per
 * day, coloured by planned work over available hours with the numbers in it.
 * Click a cell to open that person's day on the board and rebalance it.
 */
export function WorkloadView({
  days,
  today,
  resources,
  onPick,
}: {
  days: string[]
  today: string
  resources: CalendarResource[]
  onPick: (userId: string, date: string) => void
}) {
  return (
    <div className='flex min-h-0 flex-1 flex-col gap-2'>
      <div className='overflow-x-persistent min-h-0 flex-1 overflow-y-auto rounded-2xl border bg-card shadow-sm'>
        <table className='w-full border-separate border-spacing-1 p-2 text-sm'>
          <thead>
            <tr>
              <th className='sticky left-0 top-0 z-20 w-56 bg-card px-2 py-1 text-left text-xs font-medium text-muted-foreground'>
                Person
              </th>
              {days.map((d) => {
                const p = dayParts(d)
                return (
                  <th
                    key={d}
                    className={cn(
                      'sticky top-0 z-10 bg-card px-1 py-1 text-center text-xs font-medium',
                      d === today ? 'text-primary' : 'text-muted-foreground',
                    )}
                  >
                    {d === today ? 'Today' : p.weekday} {p.day}
                  </th>
                )
              })}
              <th className='sticky top-0 z-10 bg-card px-2 py-1 text-right text-xs font-medium text-muted-foreground'>Window</th>
            </tr>
          </thead>
          <tbody>
            {resources.length === 0 && (
              <tr>
                <td colSpan={days.length + 2} className='p-10 text-center text-sm text-muted-foreground'>
                  No one matches these filters.
                </td>
              </tr>
            )}
            {resources.map((r) => {
              const week = weekSummary(r)
              return (
                <tr key={r.userId}>
                  <th scope='row' className='sticky left-0 z-10 bg-card px-2 text-left font-normal'>
                    <span className='flex items-center gap-2'>
                      <span
                        className={cn(
                          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                          r.active ? avatarTone(r.name) : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {r.initials}
                      </span>
                      <span className='truncate text-[13px]'>{r.name}</span>
                    </span>
                  </th>
                  {days.map((d) => {
                    const load = r.days.find((x) => x.date === d)
                    const pct = load ? utilization(load) : null
                    return (
                      <td key={d} className='p-0'>
                        <button
                          type='button'
                          onClick={() => onPick(r.userId, d)}
                          title={
                            load
                              ? `${formatHours(load.assignedHrs)} of ${formatHours(load.availableHrs)} h planned — open this day`
                              : 'Open this day'
                          }
                          className={cn(
                            'flex h-11 w-full min-w-[3.5rem] flex-col items-center justify-center rounded-md text-xs tabular-nums transition-transform hover:scale-[1.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
                            heatClass(pct),
                            d === today && 'ring-1 ring-primary/40',
                          )}
                        >
                          {pct === null ? (
                            <span>—</span>
                          ) : (
                            <>
                              <span className='font-semibold'>{Number.isFinite(pct) ? `${pct}%` : 'Over'}</span>
                              <span className='text-[10px] opacity-80'>
                                {formatHours(load!.assignedHrs)}/{formatHours(load!.availableHrs)}
                              </span>
                            </>
                          )}
                        </button>
                      </td>
                    )
                  })}
                  <td className='px-2 text-right text-xs tabular-nums text-muted-foreground'>
                    <span className={cn(week.overloadedDays > 0 && 'font-semibold text-destructive')}>
                      {formatHours(week.assignedHrs)} / {formatHours(week.availableHrs)} h
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <ul aria-label='Workload colours' className='flex flex-wrap items-center gap-3 px-1 text-[11px] text-muted-foreground'>
        {LEGEND.map((l) => (
          <li key={l.label} className='flex items-center gap-1.5'>
            <span className={cn('h-3 w-5 rounded', heatClass(l.pct))} />
            {l.label}
          </li>
        ))}
      </ul>
    </div>
  )
}
